import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ExpectationsSection } from "./ExpectationsSection";
import { Footer } from "../Footer";
import firstfellowpartnersWhitePng1 from "../../assets/images/first-fellow-partners-white.png";
import StaggeredMenu from "../ui/StaggeredMenu";
import { SEOHead } from "../SEOHead";

export const ManifestoPage = () => {
  const desktopArrowRef = useRef(null);
  const fixedMobileArrowRef = useRef(null);
  const thirdParagraphRef = useRef(null);
  const expectationsSectionRef = useRef(null);
  const footerRef = useRef(null);
  const manifestoMainRef = useRef(null);
  const [showFixedArrow, setShowFixedArrow] = useState(true);
  const [isInSecondSection, setIsInSecondSection] = useState(false);

  const handleArrowClick = () => {
    if (isInSecondSection) {
      // Scroll back to top of manifesto section
      if (manifestoMainRef.current) {
        manifestoMainRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Scroll to ExpectationsSection
      if (expectationsSectionRef.current) {
        expectationsSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  useEffect(() => {
    // Simple scroll listener - only works on mobile
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1200;
      
      if (!isMobile) {
        setShowFixedArrow(false);
        return;
      }

      setShowFixedArrow(true);

      // Simple check: if ExpectationsSection is visible in viewport, rotate arrow
      // getBoundingClientRect().top gives distance from viewport top
      // If it's less than viewport height, we're in or past the section
      if (expectationsSectionRef.current) {
        const rect = expectationsSectionRef.current.getBoundingClientRect();
        // If section top is at or above viewport center, we're in the section
        if (rect.top <= window.innerHeight * 0.5) {
          setIsInSecondSection(true);
          return;
        }
      }

      // Also check footer
      if (footerRef.current) {
        const rect = footerRef.current.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.5) {
          setIsInSecondSection(true);
          return;
        }
      }

      setIsInSecondSection(false);
    };

    // Use a simple debounce with requestAnimationFrame
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const menuItems = [
    { label: 'Our Manifesto', ariaLabel: 'View our manifesto', link: '/manifesto' },
    { label: 'Family Members', ariaLabel: 'View family members', link: '/family' },
    { label: 'Team', ariaLabel: 'Meet our team', link: '/team' },
    { label: 'References', ariaLabel: 'View references', link: '/references' },
    { label: 'Get in touch', ariaLabel: 'Get in touch with us', link: '/contact' }
  ];

  const socialItems = [
    { label: 'Twitter', link: 'https://twitter.com' },
    { label: 'LinkedIn', link: 'https://linkedin.com' }
  ];
  const manifestoData = {
    title: "The FFP Manifesto",
    subtitle: "Backing the entrepreneurs of tomorrow",
    paragraphs: [
      {
        id: 1,
        content: [
          {
            text: "We believe tomorrow can be a better place when ",
            type: "thin",
          },
          {
            text: "entrepreneurs with bold visions and uncompromising integrity",
            type: "semibold",
          },
          {
            text: " have the freedom, resources, and partners they need to build solutions that truly matter.",
            type: "thin",
          },
        ],
      },
      {
        id: 2,
        content: [
          {
            text: "First Fellow Partners (FFP) is not a traditional VC firm. We are a private investment firm built on a simple but powerful principle — ",
            type: "thin",
          },
          { text: "a founder-first philosophy.\n\n", type: "semibold" },
          {
            text: "We invest only our own capital, which means we can devote ",
            type: "thin",
          },
          {
            text: "100% of our focus to the entrepreneurs we back",
            type: "semibold",
          },
          {
            text: " — our only external stakeholders. Unlike traditional VC firms, we don't answer to LPs, because we don't have them.",
            type: "thin",
          },
        ],
      },
      {
        id: 3,
        content: [
          {
            text: "We are proud to have created a firm grounded in ",
            type: "thin",
          },
          {
            text: "deeply held values; entrepreneurship, trust, and integrity. We believe that in the right hands, technology can solve some of the world's most pressing challenges.\n\n",
            type: "semibold",
          },
          { text: "This is why we seek to partner with ", type: "thin" },
          {
            text: "bold, mission-driven technology entrepreneurs",
            type: "semibold",
          },
          {
            text: " who are determined to build or fix something that truly matters. To us, ",
            type: "thin",
          },
          {
            text: "technology is more than innovation; it can be a force for good.",
            type: "semibold",
          },
        ],
      },
    ],
  };

  const renderTextContent = (content) => {
    return content.map((segment, index) => {
      const baseClasses =
        segment.type === "thin"
          ? "font-light"
          : "font-bold";

      if (segment.text.includes("\n")) {
        return segment.text.split("\n").map((line, lineIndex) => (
          <React.Fragment key={`${index}-${lineIndex}`}>
            <span className={baseClasses}>{line}</span>
            {lineIndex < segment.text.split("\n").length - 1 && <br />}
          </React.Fragment>
        ));
      }

  return (
        <span key={index} className={baseClasses}>
          {segment.text}
        </span>
      );
    });
  };

  useEffect(() => {
    // Fade in arrows when page loads
    const arrows = [desktopArrowRef.current, fixedMobileArrowRef.current].filter(Boolean);
    arrows.forEach((arrow, index) => {
      if (arrow) {
        gsap.fromTo(arrow,
          { opacity: 0, y: -20 },
          { opacity: 1, y: 0, duration: 1, ease: "power3.out", delay: 0.5 + (index * 0.1) }
        );
      }
    });
  }, []);

  return (
    <div className="bg-[#121212] w-full min-h-screen overflow-y-auto scrollbar-default flex flex-col items-center">
      <SEOHead
        title="The FFP Manifesto | First Fellow Partners"
        description="Our manifesto: we believe tomorrow can be a better place when entrepreneurs with bold visions have the freedom, resources, and partners to build solutions that truly matter."
        path="/manifesto"
      />
      {/* Navbar */}
        <div className="w-full bg-[#121212] border-b border-[#424242] z-50">
          <div className="flex justify-between items-center px-4 sm:px-6 md:px-8 tablet-lg:px-10 py-3 sm:py-4">
            {/* Logo on the left */}
            <Link to="/" className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] md:w-[100px] md:h-[100px] tablet-lg:w-[110px] tablet-lg:h-[110px] desktop:w-[120px] desktop:h-[120px] xl:w-[140px] xl:h-[140px]">
              <img
                className="w-full h-full object-contain"
                alt="First Fellow Partners logo"
                src={firstfellowpartnersWhitePng1}
              />
            </Link>

            {/* StaggeredMenu button on the right */}
            <div className="w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] md:w-[80px] md:h-[80px] tablet-lg:w-[90px] tablet-lg:h-[90px] desktop:w-[100px] desktop:h-[100px] xl:w-[120px] xl:h-[120px] flex items-center justify-end">
              <StaggeredMenu
                position="right"
                items={menuItems}
                socialItems={socialItems}
                displaySocials={true}
                displayItemNumbering={true}
                menuButtonColor="#fff"
                openMenuButtonColor="#000"
                changeMenuColorOnOpen={true}
                colors={['#1a1a1a', '#424242']}
                logoUrl=""
                accentColor="#000"
                isFixed={true}
                onMenuOpen={() => console.log('Menu opened')}
                onMenuClose={() => console.log('Menu closed')}
              />
            </div>
          </div>
        </div>
      <main ref={manifestoMainRef} className="flex flex-col items-center px-4 sm:px-6 md:px-8 tablet-lg:px-12 desktop:px-[5vw] wide:px-[60px] py-6 sm:py-8 md:py-[40px] mt-8 sm:mt-12 md:mt-[60px] mb-8 sm:mb-12 md:mb-[60px] relative max-w-full">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h1 className="text-white text-[28px] sm:text-[32px] md:text-[36px] tablet-lg:text-[38px] desktop:text-[40px] leading-tight font-bold">
            {manifestoData.title}
          </h1>
          <p className="text-white text-lg sm:text-xl md:text-xl tablet-lg:text-2xl font-light mt-2 sm:mt-3 md:mt-4">
            {manifestoData.subtitle}
          </p>
        </div>

        {manifestoData.paragraphs.map((paragraph, index) => {
          const paragraphClasses = [
            "relative w-full md:w-[90%] tablet-lg:w-[85%] desktop:w-[85%] wide:w-[1292px] font-light text-white text-sm sm:text-base md:text-lg tablet-lg:text-xl desktop:text-xl xl:text-2xl tracking-[0] leading-[1.4] sm:leading-[1.5] md:leading-[1.6] tablet-lg:leading-[normal] mb-4 sm:mb-5 md:mb-6",
            "relative w-full md:w-[90%] tablet-lg:w-[85%] desktop:w-[85%] wide:w-[1292px] font-light text-white text-sm sm:text-base md:text-lg tablet-lg:text-xl desktop:text-xl xl:text-2xl tracking-[0] leading-[1.4] sm:leading-[1.5] md:leading-[1.6] tablet-lg:leading-[normal] mb-4 sm:mb-5 md:mb-6",
            "relative w-full md:w-[90%] tablet-lg:w-[85%] desktop:w-[85%] wide:w-[1292px] font-light text-white text-sm sm:text-base md:text-lg tablet-lg:text-xl desktop:text-xl xl:text-2xl tracking-[0] leading-[1.4] sm:leading-[1.5] md:leading-[1.6] tablet-lg:leading-[normal]",
          ];

          return (
            <p 
              key={paragraph.id} 
              ref={index === 2 ? thirdParagraphRef : null}
              className={`${paragraphClasses[index]} overflow-visible`}
            >
              {renderTextContent(paragraph.content)}
              {/* Down arrow - positioned to the right, arrow tip aligned with end of the last sentence */}
              {index === 2 && (
                <span 
                  ref={desktopArrowRef}
                  onClick={handleArrowClick}
                  className="absolute left-[calc(100%+1.5vw)] tablet-lg:left-[calc(100%+2vw)] desktop:left-[calc(100%+2.5vw)] xl:left-[calc(100%+3vw)] wide:left-[calc(100%+4vw)] hidden desktop:block cursor-pointer hover:opacity-80 transition-opacity"
                  style={{
                    bottom: '12px', // Fixed offset from bottom to align arrow tip with paragraph bottom
                  }}
                >
                  <svg 
                    width="60" 
                    height="120" 
                    viewBox="0 0 60 120" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className="desktop:w-12 desktop:h-24 xl:w-14 xl:h-28 2xl:w-20 2xl:h-40"
                    style={{
                      display: 'block'
                    }}
                  >
                    <path 
                      d="M30 8V100M30 100L8 80M30 100L52 80" 
                      stroke="white" 
                      strokeWidth="4" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </p>
          );
        })}
      </main>

      {/* Fixed mobile arrow button in bottom right corner */}
      <button
        ref={fixedMobileArrowRef}
        onClick={handleArrowClick}
        className={`desktop:hidden fixed z-40 cursor-pointer transition-all duration-500 ${
          showFixedArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          bottom: 'max(24px, env(safe-area-inset-bottom, 0px) + 24px)',
          right: 'max(24px, env(safe-area-inset-right, 0px) + 24px)',
          transform: isInSecondSection ? 'rotate(180deg)' : 'rotate(0deg)'
        }}
        aria-label={isInSecondSection ? "Scroll to top" : "Scroll to expectations section"}
      >
        <svg 
          width="48" 
          height="48" 
          viewBox="0 0 48 120" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform duration-500"
        >
          <path 
            d="M24 8V100M24 100L8 80M24 100L40 80" 
            stroke="white" 
            strokeWidth="4" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      <div ref={expectationsSectionRef} className="mt-[80px] w-full">
        <ExpectationsSection />
      </div>

      <div ref={footerRef} className="w-full">
        <Footer />
      </div>
    </div>
  );
};
